import { Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Layout & Components
import Header from './components/Header';
import Footer from './components/Footer';
import QuickContactBar from './components/QuickContactBar';
import MobileCallBar from './components/MobileCallBar';
import ScrollToTop from './components/ScrollToTop';

// Analytics & Tracking
import './lib/metaTracking';

// Pages
import HomePage from './pages/HomePage';
import ServicesIndexPage from './pages/ServicesIndexPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import DaekcenterPage from './pages/DaekcenterPage';
import AboutPage from './pages/AboutPage';
import GalleryPage from './pages/GalleryPage';
import JobPage from './pages/JobPage';
import BilopbevaringPage from './pages/BilopbevaringPage';
import ContactPage from './pages/ContactPage';
import NotFoundPage from './pages/NotFoundPage';
import ForbiddenPage from './pages/ForbiddenPage';
import LandingContentPage from './pages/LandingContentPage';

export default function App() {
  return (
    <HelmetProvider>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-zinc-50 text-zinc-900 selection:bg-red-600 selection:text-white w-full max-w-full overflow-x-clip">
        <Header />
        <QuickContactBar />

        <main className="flex-1 pb-16 md:pb-0 w-full max-w-full overflow-x-clip">
          <Routes>
            {/* Primary Navigation Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/ydelser" element={<ServicesIndexPage />} />
            <Route path="/ydelser/:slug" element={<ServiceDetailPage />} />
            <Route path="/daekcenter" element={<DaekcenterPage />} />
            <Route path="/bilopbevaring" element={<BilopbevaringPage />} />
            <Route path="/ydelser/bilopbevaring" element={<Navigate to="/bilopbevaring" replace />} />
            <Route path="/om-os" element={<AboutPage />} />
            <Route path="/om-mc-transport" element={<Navigate to="/om-os" replace />} />
            <Route path="/galleri" element={<GalleryPage />} />
            <Route path="/chauffoer-soeges" element={<JobPage />} />
            <Route path="/charffoer-soeges" element={<Navigate to="/chauffoer-soeges" replace />} />
            <Route path="/job" element={<Navigate to="/chauffoer-soeges" replace />} />
            <Route path="/kontakt" element={<ContactPage />} />

            {/* Error Pages */}
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="/404" element={<NotFoundPage />} />

            {/* Legacy .aspx Scrape Redirects */}
            <Route path="/vi-tilbyder.aspx" element={<Navigate to="/ydelser" replace />} />
            <Route path="/vi-tilbyder/autotransport.aspx" element={<Navigate to="/ydelser/autotransport-ydelser" replace />} />
            <Route path="/vi-tilbyder/autohjaelp-24-7.aspx" element={<Navigate to="/ydelser/autohjaelp-24-7" replace />} />
            <Route path="/vi-tilbyder/bjaergningskoeretoej.aspx" element={<Navigate to="/ydelser/bjaergningskoeretoej" replace />} />
            <Route path="/vi-tilbyder/fritraekning.aspx" element={<Navigate to="/ydelser/fritraekning-ydelse" replace />} />
            <Route path="/vi-tilbyder/krantransport.aspx" element={<Navigate to="/ydelser/krantransport" replace />} />
            <Route path="/vi-tilbyder/maskintransport.aspx" element={<Navigate to="/ydelser/maskintransport" replace />} />
            <Route path="/vi-tilbyder/transport-paa-sengetrailer.aspx" element={<Navigate to="/ydelser/transport-paa-sengetrailer" replace />} />
            <Route path="/vi-tilbyder/koersel-med-gardintrailer-m-medbringertruck.aspx" element={<Navigate to="/ydelser/gardintrailer-medbringertruck" replace />} />
            <Route path="/vi-tilbyder/servicevogn.aspx" element={<Navigate to="/ydelser/servicevogn" replace />} />
            <Route path="/vi-tilbyder/godstransport.aspx" element={<Navigate to="/ydelser/godstransport" replace />} />

            {/* Alternative legacy routes */}
            <Route path="/koeretoejer/fritraekning.aspx" element={<Navigate to="/ydelser/fritraekning-ydelse" replace />} />
            <Route path="/koeretoejer/krantransport.aspx" element={<Navigate to="/ydelser/krantransport" replace />} />
            <Route path="/koeretoejer/maskintransport.aspx" element={<Navigate to="/ydelser/maskintransport" replace />} />
            <Route path="/koeretoejer/transport-paa-sengetrailer.aspx" element={<Navigate to="/ydelser/transport-paa-sengetrailer" replace />} />
            <Route path="/koeretoejer/koersel-med-gardintrailer-m-medbringertruck.aspx" element={<Navigate to="/ydelser/gardintrailer-medbringertruck" replace />} />
            <Route path="/koeretoejer/servicevogn.aspx" element={<Navigate to="/ydelser/servicevogn" replace />} />
            <Route path="/koeretoejer/bjergningskoeretoej.aspx" element={<Navigate to="/ydelser/bjaergningskoeretoej" replace />} />

            <Route path="/arkiv.aspx" element={<Navigate to="/galleri" replace />} />
            <Route path="/pladsen.aspx" element={<Navigate to="/om-os" replace />} />
            <Route path="/job.aspx" element={<Navigate to="/chauffoer-soeges" replace />} />
            <Route path="/kontakt.aspx" element={<Navigate to="/kontakt" replace />} />
            <Route path="/kontakt-os-formular.aspx" element={<Navigate to="/kontakt" replace />} />

            {/* SEO Structured Landing Pages from JSON */}
            <Route path="/autohjaelp" element={<LandingContentPage forcedPath="/autohjaelp" />} />
            <Route path="/bugsering-af-bil" element={<LandingContentPage forcedPath="/bugsering-af-bil" />} />
            <Route path="/bugsering-af-lastbil" element={<LandingContentPage forcedPath="/bugsering-af-lastbil" />} />
            <Route path="/bjaergning" element={<LandingContentPage forcedPath="/bjaergning" />} />
            <Route path="/tow-truck" element={<LandingContentPage forcedPath="/tow-truck" />} />
            <Route path="/fritraekning" element={<LandingContentPage forcedPath="/fritraekning" />} />
            <Route path="/skiltevogn" element={<LandingContentPage forcedPath="/skiltevogn" />} />
            <Route path="/TMA" element={<LandingContentPage forcedPath="/TMA" />} />
            <Route path="/tma" element={<LandingContentPage forcedPath="/TMA" />} />
            <Route path="/daekcenter-lastbil" element={<LandingContentPage forcedPath="/daekcenter-lastbil" />} />
            <Route path="/daekskift-lastbil" element={<LandingContentPage forcedPath="/daekskift-lastbil" />} />
            <Route path="/Dækcenter%20lastbil" element={<Navigate to="/daekcenter-lastbil" replace />} />
            <Route path="/Dækcenter lastbil" element={<Navigate to="/daekcenter-lastbil" replace />} />
            <Route path="/Dækskift%20lastbil" element={<Navigate to="/daekskift-lastbil" replace />} />
            <Route path="/Dækskift lastbil" element={<Navigate to="/daekskift-lastbil" replace />} />

            {/* Service aliases to rich SEO landing pages */}
            <Route path="/ydelser/autohjaelp-24-7" element={<LandingContentPage forcedPath="/autohjaelp" />} />
            <Route path="/ydelser/bugsering-ydelser" element={<LandingContentPage forcedPath="/bugsering-af-bil" />} />
            <Route path="/ydelser/bjaergningskoeretoej" element={<LandingContentPage forcedPath="/bjaergning" />} />
            <Route path="/ydelser/fritraekning-ydelse" element={<LandingContentPage forcedPath="/fritraekning" />} />
            <Route path="/ydelser/skiltevogn" element={<LandingContentPage forcedPath="/skiltevogn" />} />
            <Route path="/ydelser/tma-koeretoej" element={<LandingContentPage forcedPath="/TMA" />} />
            <Route path="/ydelser/tow-truck" element={<LandingContentPage forcedPath="/tow-truck" />} />

            {/* 404 Catch-All */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>

        <Footer />
        <MobileCallBar />
      </div>
    </HelmetProvider>
  );
}
